import type { ArticleData } from "@qendo/resources";
import * as WebBrowser from "expo-web-browser";
import { useState } from "react";
import { Image, Pressable, StyleSheet, Text, View, type ViewProps } from "react-native";
import { Title } from "~/components/layout/text";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { formatDateForHumans } from "~/util/dates";
import { setWidthOnLayout } from "~/util/layout";

export type ArticleProps = {
    article: ArticleData;
} & ViewProps;

export const ARTICLE_HEIGHT = 102;
const IMAGE_WIDTH = 140;
const COLUMN_GAP = 16;
const ROW_GAP = 8;
const DATE_FONT_SIZE = 14;
const DATE_SIZE = DATE_FONT_SIZE + 2;

export const Article = ({ article, ...viewProps }: ArticleProps) => {
    const { text } = useTheme();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth, viewProps.onLayout);
    const content_width = width - (IMAGE_WIDTH + COLUMN_GAP);

    const { link, title, published, image } = article;
    const { loading, setLoading } = useFeedback();

    const onOpen = async () => {
        if (loading) {
            return;
        }
        setLoading(true);

        WebBrowser.openBrowserAsync(link).finally(() => setLoading(false));
    };
    const fallback = require("~/assets/title-logo.png");
    const image_source = image ? { uri: image } : fallback;

    return (
        <View {...viewProps} onLayout={onLayout}>
            <Pressable onPress={onOpen} style={styles.container}>
                <Image
                    width={IMAGE_WIDTH}
                    height={ARTICLE_HEIGHT}
                    loadingIndicatorSource={fallback}
                    source={image_source}
                    style={[styles.image, { resizeMode: image ? "cover" : "contain" }]}
                    crossOrigin="anonymous"
                    alt=""
                />
                <View style={[styles.content, { width: content_width }]}>
                    <Title size="base" style={styles.title}>
                        {title}
                    </Title>
                    <Text style={[styles.date, text.primary]}>{formatDateForHumans(published, "long")}</Text>
                </View>
            </Pressable>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        height: ARTICLE_HEIGHT,
        flexDirection: "row",
        gap: COLUMN_GAP,
    },
    image: {
        width: IMAGE_WIDTH,
        height: ARTICLE_HEIGHT,
        borderRadius: 8,
    },
    content: {
        height: ARTICLE_HEIGHT,
        gap: ROW_GAP,
    },
    title: {
        maxHeight: ARTICLE_HEIGHT - ROW_GAP - DATE_SIZE - 2,
        overflow: "hidden",
    },
    date: {
        fontWeight: 400,
        fontSize: DATE_FONT_SIZE,
        lineHeight: DATE_SIZE,
    },
});
